import type * as am5xy from "@amcharts/amcharts5/xy";
import type * as am5 from "@amcharts/amcharts5";

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from "@angular/core";
import { Subscription, forkJoin } from "rxjs";
import { switchMap } from "rxjs/operators";
import { ActivatedRoute } from "@angular/router";
import { AuthService } from "src/app/services";
import { ChartService } from "../../services/chart.service";
import { DataAnalysisExportModel } from "src/app/models/dataAnalysisExport.model";
import { DataAnalysisRequestModel } from "src/app/models/dataAnalysisRequest.model";
import { DateFormatService } from "../../services/dateFormat.service";
import { DeviceDetailsMapClustererComponent } from "src/app/shared/map/device-details-map-clusterer/device-details-map-clusterer.component";
import { DeviceValue } from "src/app/models/deviceValue.model";
import { Enum } from "src/app/models/enum.model";
import { EventService } from "src/app/services/event.service";
import { LoaderService } from "../../services/loader.service";
import { Location } from "@angular/common";
import { OnZoom } from "../chartBase/onZoom.interface";
import { IMapComponent } from "../chartBase/mapComponent.interface";
import { RecordDto } from "src/app/models/recordDto.model";
import { ResizedEvent } from "angular-resize-event-package";
import { Sensor } from "src/app/models/sensor.model";
import { SensorRecord } from "src/app/models/sensorRecord.model";
import { SensorRecordChartModel } from "src/app/models/sensorRecordChart.model";
import { SensorRecordService } from "src/app/services/sensorRecord.service";
import { SensorRecordsRequestModel } from "../../models/sensorRecordsRequest.model";
import { SensorService } from "../../services/sensor.service";
import { TimeIntervalEnum } from "src/app/enums/timeInterval.enum";
import { TimeZoneService } from "src/app/services/timeZone.service";
import { ToastService } from "../../services/toast.service";
import { UserPreferences } from "src/app/models/userPreferences.model";
import type { VgApiService } from "@videogular/ngx-videogular/core";
import { VideoResource } from "src/app/models/videoResource.model";
import { ValueConversionService } from "src/app/services/value-conversion.service";
import { LocalStorageConstants } from "src/app/shared/constants/localStorage.constants";

import { trackById, trackByIndex } from "src/app/shared/utils/track-by.util";
@Component({
  standalone: false,
    selector: "app-device-detail",
    templateUrl: "./device-detail.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./device-detail.component.scss"],
})
export class DeviceDetailComponent
    implements OnInit, AfterViewInit, OnDestroy, OnZoom, IMapComponent {
    @ViewChild(DeviceDetailsMapClustererComponent)
    mapClustererComponent: DeviceDetailsMapClustererComponent;
    readonly CHART_DIV_ID = "device-detail-chart";
    readonly CHART_VIDEO_DIV_ID = "device-detail-video-chart";

    sensorId: string;
    sensorName: string;
    sensors: Sensor[];
    sensorRecord: RecordDto;
    isLoading = false;
    isMapLoading = false;
    fromDate: Date;
    toDate: Date;
    preferences: UserPreferences;
    timeZone: string;
    timeZoneLabel: string;
    chart: am5xy.XYChart;
    lastOnline: string;
    parameters: Enum[];
    selectedParams: number[];
    batteryPercentageClass: string;
    icon: string;
    deviceValue: DeviceValue;
    columnParameters: number[];
    disposer: am5.IDisposer;
    initDisposer: am5.IDisposer;
    analyticsDisposer: am5.IDisposer;
    shouldRecordAnalyticsZoom = false;
    analyticsVerticalDisposer: am5.IDisposer;
    shouldRecordVerticalZoom = false;
    private resizeTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private videoResizeTimeoutId: ReturnType<typeof setTimeout> | null = null;
    shouldRecordMapInteraction = false;
    subscriptions: Subscription[] = [];
    intervalId: any;
    private playVideoTimeoutId: ReturnType<typeof setTimeout> | undefined;
    private skipTrackingTimeoutId: ReturnType<typeof setTimeout> | undefined;
    private setVideoTimeTimeoutId: ReturnType<typeof setTimeout> | undefined;
    videoChart: am5xy.XYChart;
    videoChartSlider: am5.DataItem<am5xy.IDateAxisDataItem>;
    chartSlider: am5.DataItem<am5xy.IDateAxisDataItem>;
    vgApi: VgApiService;
    videoResources: VideoResource[];
    videoResource: VideoResource;
    shouldCreateSliderButtonEvents = false;
    videoDisposers: am5.IDisposer[] = [];
    isVideoChartHidden = false;
    resumeVideoPlay = false;
    skipTrackingAction = false;
    skipRangeTimeUpdate = false;
    isNotification = false;
    isEmailLink = false;
    loading = false;

    // Precomputed param lookups
    paramNamesById = new Map<number, string>();
    paramUnit = '';
    deviceAverage: Record<number, number> = {};
    devicePeak: Record<number, number> = {};

    constructor(
        private sensorRecordService: SensorRecordService,
        private sensorService: SensorService,
        private timeZoneService: TimeZoneService,
        private toastService: ToastService,
        private dateFormatService: DateFormatService,
        private chartService: ChartService,
        private zone: NgZone,
        private eventService: EventService,
        private route: ActivatedRoute,
        private authService: AuthService,
        private cd: ChangeDetectorRef,
        private loaderService: LoaderService,
        private location: Location,
        private valueConversionService: ValueConversionService
    ) {

        const currentUser = this.authService.getCurrentUser();
        this.timeZone = currentUser.userTimeZone;
        this.timeZoneLabel = this.timeZoneService.createTimeZoneLabel(
            this.timeZone
        );
        this.preferences = currentUser.preferences;
        this.lastOnline = "Never";
        this.sensorRecord = new RecordDto();
        this.batteryPercentageClass =
            this.chartService.getBatteryPercentage(null);
        this.icon = this.chartService.getWifiIcon(null);
        this.deviceValue = new DeviceValue();
    }

    ngOnInit() {
        this.isNotification =
            this.route.snapshot.queryParams.notification === "true";
        this.isEmailLink = this.route.snapshot.queryParams.id !== undefined;
        if (this.isEmailLink) {
            const encSensorId = window.btoa(this.route.snapshot.queryParams.id);
            localStorage.setItem(LocalStorageConstants.deviceName, encSensorId);
            const parameterType = parseInt(
                this.route.snapshot.queryParams.parameter,
                10
            );
            this.fromDate = this.timeZoneService.convertTimestampToLocalDate(
                parseInt(this.route.snapshot.queryParams.start, 10) * 1000,
                this.timeZone
            );
            this.toDate = this.timeZoneService.convertTimestampToLocalDate(
                parseInt(this.route.snapshot.queryParams.end, 10) * 1000,
                this.timeZone
            );
            this.authService.setCurrentUserNotificationDetails(
                parameterType,
                this.fromDate,
                this.toDate
            );
        }
    }

    ngAfterViewInit() {
        this.zone.runOutsideAngular(async () => {
            this.chart = await this.chartService.createChart(
                this.CHART_DIV_ID
            );
            this.chartSlider = await this.chartService.createChartRange(
                this.chart
            );
            this.videoChart = await this.chartService.createChart(
                this.CHART_VIDEO_DIV_ID
            );
            this.videoChartSlider = await this.chartService.createChartSlider(
                this.videoChart, this
            );
        });
        this.loadPage();
        this.cd.detectChanges();
    }

    ngOnDestroy() {
        if (this.resizeTimeoutId !== null) clearTimeout(this.resizeTimeoutId);
        if (this.videoResizeTimeoutId !== null) clearTimeout(this.videoResizeTimeoutId);
        clearInterval(this.intervalId);
        clearTimeout(this.playVideoTimeoutId);
        clearTimeout(this.skipTrackingTimeoutId);
        clearTimeout(this.setVideoTimeTimeoutId);

        this.subscriptions.forEach((subscription) => {
            subscription.unsubscribe();
        });

        this.zone.runOutsideAngular(() => {
            this.chartService.disposeEvent(this.disposer);
            this.chartService.disposeEvent(this.initDisposer);
            this.chartService.disposeEvent(this.analyticsDisposer);
            this.chartService.disposeEvent(this.analyticsVerticalDisposer);
            if (this.chart) {
                this.chartService.disposeChartData(this.chart);
                this.chart.dispose();
            }
            if (this.videoChart) {
                this.chartService.disposeChartData(this.videoChart);
                this.videoChart.dispose();
                this.videoChartSlider = null;
                this.chartSlider = null;
            }
        });
    }

    switchSensor(sensorId) {
        const encSensorId = window.btoa(sensorId);
        localStorage.setItem(LocalStorageConstants.deviceName, encSensorId);
        this.loadPage();
    }

    loadPage() {
        const encDeviceName = localStorage.getItem(LocalStorageConstants.deviceName);
        if (encDeviceName != null) {
            this.sensorId = window.atob(encDeviceName);
        }

        let option = "8h";
        this.loaderService.show();
        this.sensorService.getById(this.sensorId).pipe(
            switchMap((sensor) => {
                const parameterArray = [];
                sensor.parameterTypes.forEach((types) => {
                    parameterArray.push({
                        id: types,
                        value: this.chartService.getParameterName(types),
                    });
                });
                this.parameters = parameterArray;
                const defaultParams = this.chartService.getDefaultParameters();
                this.selectedParams = defaultParams.filter((x) =>
                    this.parameters.some((p) => p.id === x)
                );
                const payload = new SensorRecordsRequestModel();
                payload.sensorId = this.sensorId;
                payload.isViewByEndPoint = false;
                if (
                    this.isNotification &&
                    this.preferences.fromDate &&
                    this.preferences.toDate
                ) {
                    option = undefined;
                    this.isNotification = false;
                    this.fromDate = this.preferences.fromDate;
                    this.toDate = this.preferences.toDate;
                } else if (this.isEmailLink) {
                    option = undefined;
                    this.isEmailLink = false;
                } else {
                    const dateRange = this.timeZoneService.createDateRange(
                        option,
                        this.timeZone
                    );
                    this.fromDate = dateRange.startDate;
                    this.toDate = dateRange.endDate;
                }
                payload.startDate =
                    this.dateFormatService.formatDateTimeToString(
                        this.fromDate,
                        this.dateFormatService.dateToStringWithTimeFormat
                    );
                payload.endDate = this.dateFormatService.formatDateTimeToString(
                    this.toDate,
                    this.dateFormatService.dateToStringWithTimeFormat
                );
                payload.parameters = this.selectedParams;

                return forkJoin({
                    sensorRecords:
                        this.sensorRecordService.getSensorRecords(payload),
                    sensors: this.sensorService.getSensorLookupList(),
                    latestSensorRecord:
                        this.sensorRecordService.getLatestSensorRecordBySensor(this.sensorId),
                });
            })
        ).subscribe({
            next: (result) => {
                this.sensorName =
                    result.sensors.find((x) => x.id === this.sensorId)
                        ?.name ?? "N/A";
                this.sensors = result.sensors;

                this.sensorRecord = result.latestSensorRecord || new RecordDto();
                this.batteryPercentageClass =
                    this.chartService.getBatteryPercentage(this.sensorRecord);
                this.icon = this.chartService.getWifiIcon(this.sensorRecord);

                this.drawMaps(result.sensorRecords);
                this.updateActiveZoom(option);

                this.loaderService.hide();
            },
            error: (error) => {
                this.toastService.showError(error);
                this.loaderService.hide();
            }
        });
    }

    setRefreshInterval(option: string, inParams: number[]) {
        clearInterval(this.intervalId);
        const params = inParams.map((x) => x);
        this.intervalId = setInterval(() => {
            this.refreshData(option, params);
        }, 60000);
    }

    refreshData(option: string, params: number[]) {
        this.isMapLoading = true;
        const payload = new SensorRecordsRequestModel();
        payload.sensorId = this.sensorId;
        payload.isViewByEndPoint = false;
        const dateRange = this.timeZoneService.createDateRange(
            option,
            this.timeZone
        );
        payload.startDate = this.dateFormatService.formatDateTimeToString(
            option !== "all" ? dateRange.startDate : undefined,
            this.dateFormatService.dateToStringWithTimeFormat
        );
        payload.endDate = this.dateFormatService.formatDateTimeToString(
            option !== "all" ? dateRange.endDate : undefined,
            this.dateFormatService.dateToStringWithTimeFormat
        );
        payload.parameters = params;

        forkJoin({
            sensorRecords: this.sensorRecordService.getSensorRecords(payload),
            sensorRecord:
                this.sensorRecordService.getLatestSensorRecordBySensor(
                    this.sensorId
                ),
        }).subscribe(
            (result) => {
                this.sensorRecord = result.sensorRecord || new RecordDto();
                this.batteryPercentageClass =
                    this.chartService.getBatteryPercentage(this.sensorRecord);
                this.icon = this.chartService.getWifiIcon(this.sensorRecord);
                this.drawMaps(result.sensorRecords);
                this.isMapLoading = false;
            },
            (error) => {
                this.toastService.showError(error);
                this.isMapLoading = false;
            }
        );
    }

    createChartZoomEvent() {
        this.chartService.disposeEvent(this.disposer);
        this.disposer = this.zone.runOutsideAngular(() => {
            return this.chartService.createChartZoomEvent(this.chart, this);
        });
        this.chartService.disposeEvent(this.analyticsDisposer);
        this.analyticsDisposer = this.zone.runOutsideAngular(() => {
            return this.chartService.createChartAnalyticsEvent(
                this.chart,
                this
            );
        });
        this.chartService.disposeEvent(this.analyticsVerticalDisposer);
        this.analyticsVerticalDisposer = this.zone.runOutsideAngular(() => {
            return this.chartService.createChartAnalyticsVerticalZoomEvent(
                this.chart,
                this
            );
        });
    }

    onInitialZoom(sensorRecords: SensorRecord[]) {
        if (
            this.initDisposer &&
            !this.initDisposer.isDisposed() &&
            sensorRecords &&
            sensorRecords.length > 0
        ) {
            this.onDataZoom(sensorRecords);
        }
    }

    onZoom(sensorRecords: SensorRecord[]) {
        if (
            sensorRecords &&
            sensorRecords.length > 0 &&
            this.initDisposer &&
            !this.initDisposer.isDisposed()
        ) {
            this.initDisposer.dispose();
        }
        this.onDataZoom(sensorRecords);
    }

    onDataZoom(sensorRecords: SensorRecord[]) {
        this.zone.run(() => {
            this.deviceValue = this.chartService.createDeviceValue(
                sensorRecords,
                this.columnParameters
            );
            this.deviceAverage = this.deviceValue?.average ?? {};
            this.devicePeak = this.deviceValue?.peak ?? {};
        });

        this.updateMap(sensorRecords);

        const dateAxis = this.chart.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX>;
        const minZoomed = dateAxis.getPrivate("selectionMin") as number | undefined;
        const maxZoomed = dateAxis.getPrivate("selectionMax") as number | undefined;
        if (
            this.videoResources &&
            this.videoResources.length > 0 &&
            minZoomed &&
            maxZoomed
        ) {
            const videoDateAxis = this.videoChart.xAxes.getIndex(
                0
            ) as am5xy.DateAxis<am5xy.AxisRendererX>;
            videoDateAxis.set("min", minZoomed);
            videoDateAxis.set("max", maxZoomed);
        }
    }

    onAnalyticsHorizontalZoom() {
        if (!this.shouldRecordAnalyticsZoom) {
            this.shouldRecordAnalyticsZoom = true;
            return;
        }
    }

    onAnalyticsVerticalZoom() {
        if (!this.shouldRecordVerticalZoom) {
            this.shouldRecordVerticalZoom = true;
            return;
        }
    }

    isDisabled() {
        return (
            !this.fromDate ||
            !this.toDate ||
            !this.selectedParams ||
            this.selectedParams.length < 1
        );
    }

    zoom(option: string) {
        this.loaderService.show();
        if (option === "all") {
            this.fromDate = undefined;
            this.toDate = undefined;
        } else {
            const dateRange = this.timeZoneService.createDateRange(
                option,
                this.timeZone
            );
            if (dateRange === null) {
                this.loaderService.hide();
                return;
            }

            this.fromDate = dateRange.startDate;
            this.toDate = dateRange.endDate;
        }

        this.search(option);
    }

    search(option: string = null) {
        if (
            (option !== "all" && this.isDisabled()) ||
            (option === "all" &&
                (!this.selectedParams || this.selectedParams.length < 1)) ||
            !this.chartService.validateDateRange(
                this.fromDate,
                this.toDate
            )
        ) {
            this.loaderService.hide();
            return;
        }

        this.updateActiveZoom(option);

        this.isMapLoading = true;
        const payload = new SensorRecordsRequestModel();
        payload.sensorId = this.sensorId;
        payload.startDate = this.dateFormatService.formatDateTimeToString(
            this.fromDate,
            this.dateFormatService.dateToStringWithTimeFormat
        );
        payload.endDate = this.dateFormatService.formatDateTimeToString(
            this.toDate,
            this.dateFormatService.dateToStringWithTimeFormat
        );
        payload.parameters = this.selectedParams;
        payload.isViewByEndPoint = false;
        this.getSensorRecords(payload);
    }

    updateActiveZoom(option: string) {
        const zoomButtons = document.getElementById("zoom-buttons");
        const activeButtons = zoomButtons.getElementsByClassName("active");
        if (activeButtons.length > 0) {
            const activeButton = activeButtons[0];
            activeButton.classList.remove("active", "button-accent");
            activeButton.classList.add("button-primary");
        }

        if (!option) {
            return;
        }
        const newActiveButton = zoomButtons.getElementsByClassName(
            "zoom-" + option
        )[0];
        newActiveButton.classList.remove("button-primary");
        newActiveButton.classList.add("active", "button-accent");
    }

    getSensorRecords(payload: SensorRecordsRequestModel) {
        this.sensorRecordService.getSensorRecords(payload).subscribe(
            (sensorRecords) => {
                this.drawMaps(sensorRecords);
                this.loaderService.hide();
                this.isMapLoading = false;
            },
            (error) => {
                this.toastService.showError(error);
                this.isMapLoading = false;
                this.loaderService.hide();
            }
        );
    }

    drawMaps(sensorRecordChartModel: SensorRecordChartModel) {
        const sensorRecords = sensorRecordChartModel?.sensorRecords || [];
        const videoResources = sensorRecordChartModel?.videoResources || [];

        this.columnParameters = JSON.parse(JSON.stringify(this.selectedParams));

        this.paramNamesById = new Map<number, string>(
            (this.parameters || []).map(p => [p.id, this.chartService.getParameterName(p.id)])
        );
        this.paramUnit = this.columnParameters && this.columnParameters.length > 0
            ? this.valueConversionService.getParameterUnit(this.columnParameters[0])
            : '';

        sensorRecords.forEach((record) => {
            record.timestamp *= 1000;
        });
        videoResources.forEach((resource) => {
            resource.timestampStart *= 1000;
            resource.timestampEnd *= 1000;
        });

        this.shouldRecordAnalyticsZoom = false;
        this.shouldRecordVerticalZoom = false;

        this.videoResources = videoResources;
        this.isVideoChartHidden = false;
        this.zone.runOutsideAngular(async () => {
            await this.chartService.drawSensorRecordChart(
                this.chart,
                sensorRecords,
                this.selectedParams,
                this,
                [],
                null,
                null,
                null,
                "device-detail"
            );
            this.createChartZoomEvent();

            this.chartService.disposeEvent(this.initDisposer);
            this.initDisposer = this.zone.runOutsideAngular(() => {
                return this.chartService.createChartInitialZoomEvent(
                    this.chart,
                    this,
                    false
                );
            });
            if (videoResources && videoResources.length > 0) {
                await this.chartService.drawVideoChart(
                    this.videoChart,
                    videoResources
                );
                this.videoResource = videoResources[0];
                if (this.vgApi) {
                    this.videoDisposers =
                        this.chartService.createChartSliderButtonEvents(
                            this.videoChartSlider,
                            this.videoResource.timestampStart,
                            this.videoChart,
                            this.videoDisposers,
                            this
                        );
                    this.vgApi.currentTime = 0;
                } else {
                    this.shouldCreateSliderButtonEvents = true;
                }
            } else {
                this.videoResource = null;
            }
            this.loaderService.hide();
        });

        if (!videoResources || videoResources.length === 0) {
            this.isVideoChartHidden = true;
        }
    }

    back(): void {
        this.location.back();
    }

    export() {
        if (
            !this.fromDate ||
            !this.toDate ||
            !this.chartService.validateDateRange(
                this.fromDate,
                this.toDate
            )
        ) {
            return;
        }

        this.isMapLoading = true;
        const payload = new DataAnalysisExportModel();
        const model = new DataAnalysisRequestModel();
        model.isViewByEndPoint = false;
        model.sensorId = this.sensorId;
        model.name = this.sensorName;
        model.startDate = this.dateFormatService.formatDateTimeToString(
            this.fromDate, this.dateFormatService.dateToStringWithTimeFormat
        );
        model.endDate = this.dateFormatService.formatDateTimeToString(
            this.toDate, this.dateFormatService.dateToStringWithTimeFormat
        );
        model.parameters = this.selectedParams;
        model.timeIntervalId = TimeIntervalEnum.None;
        model.selectedDevices = [`1+${this.sensorId}`];
        model.includeGps = true;
        payload.models = [model];

        this.sensorRecordService.exportParameters(payload).subscribe(
            (blob) => {
                this.chartService.downloadFile(blob);
                this.isMapLoading = false;
            },
            (error) => {
                this.toastService.showError(error);
                this.isMapLoading = false;
            }
        );
    }

    updateMap(
        sensorRecords: SensorRecord[],
        shouldRecordBulletClick: boolean = false
    ) {
        if (!this.preferences.showMap) {
            return;
        }
        this.shouldRecordMapInteraction = false;
        const sensors: Sensor[] = [
            { id: this.sensorId, name: this.sensorName },
        ];
        this.mapClustererComponent.updateMarkerClusterer(
            sensorRecords,
            sensors
        );
    }

    getParamName(paramId: number) {
        return this.chartService.getParameterName(paramId);
    }

    getParamUnit() {
        return this.columnParameters && this.columnParameters.length > 0
            ? this.valueConversionService.getParameterUnit(
                  this.columnParameters[0]
              )
            : "";
    }

    getValue(id: number, paramId: number) {
        return id === 0
            ? this.deviceValue.average[paramId]
            : this.deviceValue.peak[paramId];
    }

    onResized(event: ResizedEvent) {
        if (this.resizeTimeoutId !== null) {
            clearTimeout(this.resizeTimeoutId);
        }
        this.resizeTimeoutId = setTimeout(() => {
            this.resizeTimeoutId = null;
            this.chartService.onResized(this.chart, event, this.CHART_DIV_ID);
        }, 200);
    }

    onMapIdle() {
        if (!this.shouldRecordMapInteraction) {
            this.shouldRecordMapInteraction = true;
            return;
        }
    }


    onVideoResized(event: ResizedEvent) {
        if (this.videoResizeTimeoutId !== null) {
            clearTimeout(this.videoResizeTimeoutId);
        }
        this.videoResizeTimeoutId = setTimeout(() => {
            this.videoResizeTimeoutId = null;
            this.chartService.onResized(this.videoChart, event, this.CHART_VIDEO_DIV_ID);
        }, 200);
    }

    onPlayerReady(api: VgApiService) {
        this.vgApi = api;

        if (this.shouldCreateSliderButtonEvents) {
            this.zone.runOutsideAngular(() => {
                this.videoDisposers =
                    this.chartService.createChartSliderButtonEvents(
                        this.videoChartSlider,
                        this.videoResource.timestampStart,
                        this.videoChart,
                        this.videoDisposers,
                        this
                    );
            });
        }

        this.subscriptions.push(
            api.subscriptions.timeUpdate.subscribe((event) => {
                if (!this.videoResource || !this.videoResource.timestampStart) {
                    return;
                }
                let currentTime =
                    this.videoResource.timestampStart +
                    event.srcElement.currentTime * 1000;
                currentTime = Math.max(
                    this.videoResource.timestampStart,
                    currentTime
                );
                currentTime = Math.min(
                    this.videoResource.timestampEnd,
                    currentTime
                );
                if (!this.skipRangeTimeUpdate) {
                    this.videoChartSlider.set("value", currentTime);
                    this.chartSlider.set("value", currentTime);
                }
                const videoDateAxis = this.videoChart.xAxes.getIndex(
                    0
                ) as am5xy.DateAxis<am5xy.AxisRendererX>;
                const videoMin = videoDateAxis.get("min");
                const videoMax = videoDateAxis.get("max");
                if (videoMin && videoMin > currentTime) {
                    videoDateAxis.set("min", currentTime - 5000);
                }
                if (videoMax && videoMax < currentTime) {
                    videoDateAxis.set("max", currentTime + 5000);
                }
            })
        );

        this.subscriptions.push(
            api
                .getDefaultMedia()
                .subscriptions.ended.subscribe(this.nextVideo.bind(this))
        );

        this.subscriptions.push(
            api
                .getDefaultMedia()
                .subscriptions.play.subscribe(() =>
                    this.onVgApiAction(true)
                )
        );

        this.subscriptions.push(
            api
                .getDefaultMedia()
                .subscriptions.pause.subscribe(() =>
                    this.onVgApiAction(false)
                )
        );
    }

    onVgApiAction(shouldResume: boolean) {
        if (this.skipTrackingAction) {
            return;
        }
        this.resumeVideoPlay = shouldResume;
    }

    nextVideo() {
        const index = this.videoResources.indexOf(this.videoResource);

        if (index >= 0 && index < this.videoResources.length - 1) {
            this.videoResource = this.videoResources[index + 1];
            this.videoDisposers =
                this.chartService.createChartSliderButtonEvents(
                    this.videoChartSlider,
                    this.videoResource.timestampStart,
                    this.videoChart,
                    this.videoDisposers,
                    this
                );
            clearTimeout(this.playVideoTimeoutId);
            this.playVideoTimeoutId = setTimeout(() => this.playVideo(), 200);
        }
    }

    playVideo() {
        this.skipTrackingAction = true;
        this.vgApi.play();
        this.resumeVideoPlay = true;
        clearTimeout(this.skipTrackingTimeoutId);
        this.skipTrackingTimeoutId = setTimeout(() => (this.skipTrackingAction = false), 50);
    }

    jumpToVideoTime(timestamp: number) {
        if (timestamp === 0) {
            this.skipTrackingAction = true;
            this.vgApi.pause();
            this.resumeVideoPlay = false;
            clearTimeout(this.skipTrackingTimeoutId);
            this.skipTrackingTimeoutId = setTimeout(() => (this.skipTrackingAction = false), 50);
            return;
        }
        this.skipTrackingAction = false;

        let resourceUpdated = false;
        if (
            !this.videoResource.url ||
            timestamp < this.videoResource.timestampStart ||
            timestamp > this.videoResource.timestampEnd
        ) {
            const resource = this.videoResources.find(
                (r) =>
                    r.timestampStart <= timestamp && r.timestampEnd >= timestamp
            );

            if (!resource) {
                this.videoResource = new VideoResource();
                this.chartSlider.set("value", timestamp);
                return;
            }

            this.skipRangeTimeUpdate = true;
            this.videoResource = resource;
            this.videoDisposers =
                this.chartService.createChartSliderButtonEvents(
                    this.videoChartSlider,
                    this.videoResource.timestampStart,
                    this.videoChart,
                    this.videoDisposers,
                    this,
                    false
                );
            this.videoChartSlider.set("value", timestamp);
            this.chartSlider.set("value", timestamp);
            resourceUpdated = true;
        }

        if (resourceUpdated) {
            clearTimeout(this.setVideoTimeTimeoutId);
            this.setVideoTimeTimeoutId = setTimeout(() => this.setVideoTime(timestamp), 200);
        } else {
            this.setVideoTime(timestamp);
        }
    }

    setVideoTime(timestamp: number) {
        const totalTime =
            this.videoResource.timestampEnd - this.videoResource.timestampStart;
        let timeElapsed = timestamp - this.videoResource.timestampStart;
        timeElapsed = Math.max(0, timeElapsed);
        timeElapsed = Math.min(totalTime, timeElapsed);
        this.vgApi.currentTime = Math.min(
            this.vgApi.time.total,
            timeElapsed / 1000
        );
        this.skipRangeTimeUpdate = false;

        if (this.resumeVideoPlay) {
            this.playVideo();
        }
    }

    public trackById = trackById;
    public trackByIndex = trackByIndex;
}
